import { useQuery } from "convex/react";
import { api } from "../convex/_generated/api";
import { Id } from "../convex/_generated/dataModel";
import { modulesTypes } from "../convex/system/permissions";
import { userRoles } from "../convex/system/users";

export type Action = "read" | "create" | "update" | "delete";
export type Module = typeof modulesTypes.type;
export type Role = typeof userRoles.type;

export function usePermissions(module?: Module) {
  // Obtener contexto del usuario (incluye empresa y rol)
  const userContext = useQuery(api.system.context.queries.checkUserContext);

  // Obtener permisos basados en el contexto del usuario
  // Solo ejecutar si hay contexto válido Y tiene suscripción (o es super admin)
  const shouldFetchPermissions =
    userContext &&
    !("error" in userContext) &&
    userContext !== null &&
    (userContext.isSuperAdmin || userContext.subscription);

  const permissions = useQuery(
    api.system.permissions.queries.getUserPermissions,
    shouldFetchPermissions ? { module } : "skip",
  );
  const can = (action: Action, targetModule?: Module): boolean => {
    // Si no hay contexto del usuario, es null, o es un error, denegar acceso
    if (!userContext || userContext === null || "error" in userContext) {
      return false;
    }

    // Type guard para verificar que userContext tiene isSuperAdmin
    const hasValidContext = (
      context: typeof userContext,
    ): context is Exclude<
      typeof userContext,
      null | { error: string; type: string }
    > => {
      return context !== null && !("error" in context);
    };

    if (!hasValidContext(userContext)) {
      return false;
    }

    // Super admin tiene acceso total - verificar primero
    if (userContext.isSuperAdmin) {
      return true;
    }

    // Para no super_admins, verificar si tiene rol
    if (!userContext.role) {
      return false;
    }

    // Si no hay permisos cargados, denegar acceso por seguridad
    if (!permissions || permissions.length === 0) {
      return false;
    }

    const moduleToCheck = targetModule || module;
    if (!moduleToCheck) return false;

    // Buscar el permiso agrupado para el módulo
    const modulePermission = permissions.find(
      (p) => p.module === moduleToCheck,
    );

    if (!modulePermission) {
      return false;
    }

    // Mapear la acción al campo booleano correspondiente
    switch (action) {
      case "read":
        return modulePermission.canRead;
      case "create":
        return modulePermission.canCreate;
      case "update":
        return modulePermission.canUpdate;
      case "delete":
        return modulePermission.canDelete;
      default:
        return false;
    }
  };

  const canAny = (actions: Action[], targetModule?: Module): boolean => {
    return actions.some((action) => can(action, targetModule));
  };

  const canAll = (actions: Action[], targetModule?: Module): boolean => {
    return actions.every((action) => can(action, targetModule));
  };

  const getPermission = (targetModule?: Module) => {
    if (!permissions) return null;

    const moduleToCheck = targetModule || module;
    if (!moduleToCheck) return null;

    // Retorna el permiso agrupado completo del módulo
    return permissions.find((p) => p.module === moduleToCheck);
  };

  // Helper adicional: obtener todas las acciones permitidas para un módulo
  const getAllowedActions = (targetModule?: Module): Action[] => {
    const moduleToCheck = targetModule || module;
    if (!moduleToCheck) return [];

    const modulePermission = permissions?.find(
      (p) => p.module === moduleToCheck,
    );

    if (!modulePermission) return [];

    const allowed: Action[] = [];
    if (modulePermission.canRead) allowed.push("read");
    if (modulePermission.canCreate) allowed.push("create");
    if (modulePermission.canUpdate) allowed.push("update");
    if (modulePermission.canDelete) allowed.push("delete");

    return allowed;
  };

  const noPermissions = permissions === undefined || userContext === undefined;

  return {
    permissions,
    can,
    canAny,
    canAll,
    getPermission,
    getAllowedActions, // Nuevo helper
    isLoading: noPermissions,
    noPermissions,
    userContext,
    hasRole:
      userContext !== undefined &&
      userContext !== null &&
      !("error" in userContext) &&
      "role" in userContext &&
      userContext.role !== undefined,
    userRole:
      userContext &&
      userContext !== null &&
      !("error" in userContext) &&
      "role" in userContext &&
      userContext.role
        ? userContext.role
        : undefined,
    currentUser: userContext,
  };
}
